/**
 * L'éditeur d'un bon d'intervention (lot I1.1, ADR-0014) : on saisit un bon DÉJÀ réalisé, après coup.
 *
 * Il est volontairement COURT — en-tête, puis les rubriques DU MODÈLE DE FEUILLE de la nature choisie (entretien, dépannage,
 * installation, livraison, location : `domaine/bon-modeles.ts`), dans l'ordre que Benjamin a validé sur les ébauches du
 * 2026-10-16, et rien d'autre : plus de bloc « pièces remplacées » (les articles font foi), plus de signature numérique.
 * Les contrôles sont une liste PAR NATURE que l'on règle à l'écran (jamais cochés d'office : un contrôle fait se coche à la
 * main).
 *
 * Rien n'est jamais présélectionné — ni la nature, ni la date, ni « sous contrat / facturé », ni le palier — et rien n'est
 * écrit ailleurs que dans le brouillon : tant que le bon n'est pas validé, il n'écrit NI relevé de compteur NI entretien.
 * Le numéro n'est alloué qu'à la PREMIÈRE sauvegarde (le serveur le rend avec le bon).
 *
 * La machine est obligatoire, SAUF pour une livraison, qui peut se faire sur un site seul : l'éditeur ne propose alors que
 * les natures dont le modèle le permet, et ne lit ni compteur ni gamme.
 *
 * Les contrôles de l'écran ne font qu'éviter un aller-retour voué au refus : le serveur reste l'autorité, et
 * ses refus (une liste de messages à la soumission) s'affichent dans la barre du bas, là où l'on a cliqué.
 * L'erreur est posée APRÈS la relecture, qui ne l'efface pas.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  ApiError,
  api,
  type Compteur,
  type BonDetail,
  type ChoixDossier,
  type FacturationBon,
  type GammeNiveau,
  type GenreLigneBon,
  type GenreSuiteBon,
  type MaintenanceMachine,
  type NatureBon,
  type TechnicienBon,
} from '../api/client';
import {
  OPTIONS_RELEVE,
  PLAFOND_TECHNICIENS,
  ajouterLigneBon,
  ajouterSuite,
  ajouterTechnicien,
  avecComptesCrees,
  basculerControle,
  bonVide,
  cleNomTechnicien,
  estEnPlus,
  palierVerse,
  changerTypeLigne,
  cocherTousLesControles,
  compteursPourSaisie,
  controlesDeLaNature,
  deplacerLigneBon,
  entreeCreation,
  entreeSauvegarde,
  estCompteurACreer,
  estModifie,
  estReleveEnHeures,
  lireDecimal,
  majLigneBon,
  majReleve,
  majSuite,
  nombreControlesFaits,
  paliersDeLaGamme,
  problemesDeSaisie,
  problemesDeSoumission,
  retirerLigneBon,
  retirerSuite,
  retirerTechnicien,
  techniciensAProposer,
  valeurAffichee,
  valeursACreer,
  jourProposeDuPlanning,
  proposerDepuisPlanning,
  proposerLignesDuPlanning,
  versEditable,
  type BonEditable,
  type DetailsEditable,
} from '../domaine/bon-edition';
import {
  CHOIX_DOSSIER,
  FACTURATIONS,
  LIBELLE_DOSSIER,
  LIBELLE_FACTURATION,
  configDe,
  libelleTechniciens,
  modeleDe,
  type Rubrique,
} from '../domaine/bon-modeles';
import {
  appliquerLeControle,
  choisirUnCandidat,
  confirmationDuRemplacement,
  lignesDuPalier,
  lignesVerseesParLeBouton,
  phraseDesPiecesAjoutees,
  phraseDuVerdict,
  phraseDuVersement,
  verdictAffiche,
  verserLesLignes,
  type BilanControle,
  type Verdicts,
} from '../domaine/bon-gamme';
import {
  ajouterLigneHeures,
  heuresEnvoyees,
  lignesHeures,
  majDuree,
  majLigneAjoutee,
  quartDHeure,
  retirerLigneHeures,
  totalMinutes,
} from '../domaine/bon-heures';
import { nouvelleCle } from '../domaine/gammes-edition';
import { dureeFr } from '../domaine/planning';
import { lireHeuresSaisies } from '../domaine/planning-interventions';
import { signalerModifications } from '../domaine/garde-modifications';
import { GENRES_SUITE, LIBELLE_GENRE_LIGNE, LIBELLE_SUITE, aujourdhuiLocal, interventionAFaire, jourFr } from '../domaine/interventions';
import { useDroitsBons } from '../auth/useDroits';
import { BlocGammeBon, type GammeChoisie } from './BlocGammeBon';
import { ChiffrageBon } from './ChiffrageBon';
import { ChampReference } from './ChampReference';
import { ErreurFormulaire } from './Formulaire';

export interface ResultatEnregistrement {
  readonly bon: BonDetail;
  /** Le bon vient d'être CRÉÉ (son numéro vient d'être alloué). */
  readonly creation: boolean;
  /** Le bon a aussi été soumis à validation. */
  readonly soumis: boolean;
  /**
   * La soumission a été REFUSÉE (bon incomplet, révision périmée…) alors que le brouillon, lui, est enregistré.
   * Le parent annonce le refus APRÈS avoir relu le bon : une erreur posée dans l'éditeur serait effacée par le
   * rechargement qui suit.
   */
  readonly refusSoumission?: ApiError | Error;
}

/** La valeur de l'option « Autre technicien… » du menu (aucun nom ne peut s'écrire ainsi : pas de lettre). */
const CHOIX_AUTRE_TECHNICIEN = '__autre__';

export function EditeurBon({
  bon,
  assetId,
  siteId = null,
  libelleCible,
  natures,
  techniciens,
  onEnregistre,
  onFermer,
  onConflit,
}: {
  /** Le bon à modifier ; `null` = un bon neuf (pas encore de numéro). */
  bon: BonDetail | null;
  /** La machine du bon (déjà choisie pour un bon neuf) ; `null` pour une livraison faite sur un site seul. */
  assetId: string | null;
  /** Le site d'une livraison sans machine (bon neuf) ; ignoré quand une machine est choisie. */
  siteId?: string | null;
  /** Le nom de la machine ou du site sur lequel le bon est saisi. */
  libelleCible: string;
  natures: readonly NatureBon[];
  techniciens: readonly TechnicienBon[];
  /** Le bon est enregistré : le parent relit, annonce, et (à la création) bascule sur le bon créé. */
  onEnregistre: (resultat: ResultatEnregistrement) => void;
  onFermer: () => void;
  /** Le bon a été modifié par quelqu'un d'autre : le relire (le travail non enregistré est perdu). */
  onConflit: () => void;
}) {
  const origine = useMemo(
    () => (bon ? versEditable(bon) : bonVide()),
    [bon],
  );
  // « Saisir le bon » sur une intervention du planning : le jour et l'équipe prévus sont PROPOSÉS d'emblée. Comme `origine` reste
  // l'état enregistré, la proposition compte pour une modification à enregistrer : on la voit et on la garde ou on la change.
  const droitsBons = useDroitsBons();
  const [courant, setCourant] = useState<BonEditable>(() => (bon ? proposerDepuisPlanning(origine, bon, aujourdhuiLocal()) : origine));
  // Un bon sans contenu enregistré : les pièces que le planning prévoit (palier choisi) en sont les articles de départ.
  useEffect(() => {
    if (!bon || !interventionAFaire(bon.status) || bon.content !== null) return;
    let annule = false;
    api
      .planningBriefingIntervention(bon.id)
      .then((b) => {
        if (!annule) setCourant((c) => proposerLignesDuPlanning(c, bon, b.lignesPrevues.lignes, nouvelleCle));
      })
      .catch(() => undefined); // sans ces pièces le bon s'ouvre quand même : le planning les montre, on peut les ajouter à la main
    return () => {
      annule = true;
    };
  }, [bon]);
  const [compteurs, setCompteurs] = useState<Compteur[]>([]);
  const [compteursIndisponibles, setCompteursIndisponibles] = useState<string | null>(null);
  const [maintenance, setMaintenance] = useState<MaintenanceMachine | null>(null);
  // Le constructeur de la machine (marque, à défaut fabricant) : ses gammes se proposent pour verser les pièces d'un palier.
  const [constructeurId, setConstructeurId] = useState<string | null>(null);
  // Le contrôle au tarif des lignes versées d'une gamme : un repère d'écran, jamais enregistré, qui disparaît dès que la référence de
  // la ligne change. (D'où vient une ligne, la pastille « gamme B », est enregistré avec le brouillon : `ligne.versee`.)
  const [verdicts, setVerdicts] = useState<Verdicts>({});
  const [messageGamme, setMessageGamme] = useState<{ readonly texte: string; readonly attention: boolean } | null>(null);
  const [controleEnCours, setControleEnCours] = useState(false);
  const [chargement, setChargement] = useState(assetId !== null);
  const [problemes, setProblemes] = useState<string[]>([]);
  const [erreurApi, setErreurApi] = useState<ApiError | Error | null>(null);
  const [conflit, setConflit] = useState(false);
  const [enCours, setEnCours] = useState(false);
  // Une clé d'idempotence PAR ouverture de l'éditeur : un double clic sur « Enregistrer » ne crée qu'un bon.
  const cleDeCreation = useRef(nouvelleCle());

  // Le nom en cours de saisie quand on a choisi « Autre technicien… » ; `null` = le menu est affiché.
  const [autreNom, setAutreNom] = useState<string | null>(null);

  const modifie = estModifie(origine, courant);
  const aujourdhui = aujourdhuiLocal();
  const techniciensAChoisir = techniciensAProposer(techniciens, courant);

  // Les compteurs et la gamme de LA machine du bon : ce que l'on peut relever, et le palier que l'on peut déclarer.
  // Une livraison sur un site seul n'a ni l'un ni l'autre.
  useEffect(() => {
    if (assetId === null) {
      setCompteurs([]);
      setCompteursIndisponibles(null);
      setMaintenance(null);
      setConstructeurId(null);
      setChargement(false);
      return;
    }
    let annule = false;
    setChargement(true);
    void (async () => {
      try {
        const liste = await api.counters(assetId);
        if (!annule) {
          setCompteurs(compteursPourSaisie(liste));
          setCompteursIndisponibles(null);
        }
      } catch (e) {
        if (!annule) {
          setCompteurs([]);
          setCompteursIndisponibles(
            e instanceof ApiError && e.status === 403
              ? 'Vous n’avez pas le droit de lire les compteurs : le bon se saisit sans relevé.'
              : 'Les compteurs de la machine n’ont pas pu être lus : le bon se saisit sans relevé.',
          );
        }
      }
      try {
        const g = await api.assetMaintenance(assetId);
        if (!annule) setMaintenance(g);
      } catch {
        if (!annule) setMaintenance(null);
      }
      try {
        const machine = await api.asset(assetId);
        if (!annule) setConstructeurId(machine.brandPartyId ?? machine.manufacturerPartyId ?? null);
      } catch {
        // Sans le constructeur, seule la gamme de la machine se propose : le bon se saisit quand même.
        if (!annule) setConstructeurId(null);
      }
      if (!annule) setChargement(false);
    })();
    return () => {
      annule = true;
    };
  }, [assetId]);

  // Du travail non enregistré : le menu et la fermeture de l'onglet doivent le savoir.
  useEffect(() => {
    signalerModifications(modifie, 'Vous avez des modifications non enregistrées dans ce bon d’intervention.\n\nQuitter maintenant les fera perdre.');
    return () => signalerModifications(false);
  }, [modifie]);

  useEffect(() => {
    if (!modifie) return;
    const avantFermeture = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avantFermeture);
    return () => window.removeEventListener('beforeunload', avantFermeture);
  }, [modifie]);

  // Une nature archivée ne se propose plus (sauf celle que le bon porte déjà : il garde son modèle de feuille) ; sans machine,
  // seules les natures dont le modèle le permet (la livraison) sont proposées.
  const naturesProposees = useMemo(() => {
    const actives = natures.filter((n) => !n.archived || n.id === courant.natureId);
    return assetId === null ? actives.filter((n) => configDe(modeleDe(n)).machineFacultative) : actives;
  }, [natures, assetId, courant.natureId]);
  const nature = natures.find((n) => n.id === courant.natureId) ?? null;
  const modele = modeleDe(nature);
  const config = configDe(modele);
  // Les lignes de « Valeurs relevées » : les compteurs de la machine, plus les valeurs standard de la feuille (T°, pression,
  // tension…) que la machine n'a pas encore — celles-là se créent à l'enregistrement, si une valeur y est saisie.
  const lignesCompteurs = useMemo(
    // Sans la lecture des compteurs (droit manquant, panne) rien ne se relève : ni compteurs, ni valeurs standard à créer.
    () => (assetId === null || compteursIndisponibles !== null ? [] : compteursPourSaisie(compteurs, nature?.valeursStandard ?? [], assetId)),
    [compteurs, nature, assetId, compteursIndisponibles],
  );
  const paliers = paliersDeLaGamme(maintenance);
  const gammeId = maintenance?.rattachement?.profile.id;
  const contexte = {
    modele,
    compteurs: lignesCompteurs,
    natureEnregistreEntretien: nature?.recordsService ?? false,
    paliers,
    aujourdhui,
    // Une intervention planifiée se prépare à l'avance : son bon porte le jour prévu, même à venir (il se soumettra ce jour-là).
    dateFuturePermise: bon != null && interventionAFaire(bon.status),
  };

  const modifier = (fn: (b: BonEditable) => BonEditable) => {
    setCourant(fn);
    setProblemes([]);
    setErreurApi(null);
  };

  // Les derniers états rendus : le contrôle des références au tarif répond APRÈS un aller-retour, il s'applique à ce que l'écran
  // montre à ce moment-là (une ligne retapée entre-temps n'est jamais touchée).
  const courantRef = useRef(courant);
  courantRef.current = courant;
  const verdictsRef = useRef(verdicts);
  verdictsRef.current = verdicts;

  /**
   * Verse les pièces d'un palier de gamme dans les articles, puis contrôle leurs références au tarif : « on garde la référence
   * d'origine, sauf si elle n'est plus au tarif ; on la remplace par la nouvelle » — toute seule quand c'est sûr, sinon la ligne
   * est signalée avec ses candidats. Un contrôle qui échoue n'empêche rien : les pièces sont déjà dans le bon.
   */
  const verserPieces = async (niveau: GammeNiveau, gamme: GammeChoisie) => {
    if (assetId === null) return;
    const nouvelles = lignesDuPalier(niveau, gamme.id);
    if (nouvelles.length === 0) return;

    // Les paliers sont cumulatifs : verser un palier REMPLACE ce que le bouton avait versé (les lignes saisies à la main restent).
    const retirees = lignesVerseesParLeBouton(courantRef.current).length;
    const question = confirmationDuRemplacement(courantRef.current, { code: niveau.code, pieces: nouvelles.length });
    if (question !== null && !window.confirm(`${question}\n\nRemplacer ?`)) return;

    modifier((b) => verserLesLignes(b, nouvelles));
    setMessageGamme(null);

    const aControler = nouvelles.filter((l) => l.reference !== '');
    if (aControler.length === 0) {
      setMessageGamme({ texte: phraseDesPiecesAjoutees(nouvelles.length, niveau.code, retirees), attention: false });
      return;
    }

    setControleEnCours(true);
    let bilan: BilanControle | null = null;
    try {
      const resultat = await api.verifierReferencesBon(
        assetId,
        aControler.map((l) => ({ reference: l.reference, designation: l.designation })),
      );
      const controle = appliquerLeControle(
        courantRef.current,
        aControler.map((l) => ({ lineKey: l.lineKey, reference: l.reference })),
        resultat,
        verdictsRef.current,
      );
      bilan = controle;
      modifier(() => controle.bon);
      setVerdicts(controle.verdicts);
    } catch {
      bilan = null;
    } finally {
      setControleEnCours(false);
    }
    setMessageGamme({
      texte: phraseDuVersement(nouvelles.length, niveau.code, bilan, retirees),
      attention: bilan === null || bilan.aChoisir > 0,
    });
  };

  /** Choisir un candidat du tarif pour une ligne dont la référence n'y est plus. */
  const choisirCandidat = (lineKey: string, candidat: { reference: string; description: string }) => {
    const r = choisirUnCandidat(courantRef.current, lineKey, candidat, verdictsRef.current);
    modifier(() => r.bon);
    setVerdicts(r.verdicts);
  };

  const majDetails = (changement: Partial<DetailsEditable>) =>
    modifier((b) => ({ ...b, details: { ...b.details, ...changement } }));

  /** Un nom tapé via « Autre technicien… » : il rejoint le bon (rien si vide ou déjà présent) et le menu revient. */
  const validerAutreTechnicien = () => {
    if (autreNom !== null && autreNom.trim() !== '') modifier((b) => ajouterTechnicien(b, autreNom, techniciens));
    setAutreNom(null);
  };

  const enregistrer = async (soumettre: boolean) => {
    // Un nom tapé dans « Autre technicien… » sans avoir cliqué « Ajouter » ne doit pas disparaître en silence.
    const bonAEnvoyer = autreNom === null ? courant : ajouterTechnicien(courant, autreNom, techniciens);
    if (autreNom !== null) {
      setCourant(bonAEnvoyer);
      setAutreNom(null);
    }
    const trouves = soumettre
      ? problemesDeSoumission(bonAEnvoyer, contexte)
      : problemesDeSaisie(bonAEnvoyer, contexte);
    if (bonAEnvoyer.natureId === '') trouves.unshift('Choisissez la nature du bon.');
    if (trouves.length > 0) {
      setProblemes(trouves);
      setErreurApi(null);
      return;
    }

    setEnCours(true);
    setErreurApi(null);
    setConflit(false);

    let enregistre: BonDetail;
    try {
      // Les valeurs standard saisies (T° ambiante…) que la machine n'a pas encore : leurs compteurs se créent d'abord, puis le bon
      // porte un relevé dessus. Rejouable sans risque : un compteur déjà créé est réutilisé, jamais dupliqué.
      let envoye = bonAEnvoyer;
      let compteursEnvoyes: readonly Compteur[] = lignesCompteurs;
      const cles = assetId === null ? [] : valeursACreer(bonAEnvoyer, lignesCompteurs);
      if (assetId !== null && cles.length > 0) {
        const { compteurs: crees } = await api.creerCompteursStandard(assetId, { natureId: bonAEnvoyer.natureId, keys: cles });
        const remplace = avecComptesCrees(bonAEnvoyer, lignesCompteurs, crees);
        envoye = remplace.bon;
        compteursEnvoyes = remplace.compteurs;
      }
      enregistre = bon
        ? await api.sauvegarderBon(bon.id, entreeSauvegarde(envoye, bon.revision, compteursEnvoyes))
        : await api.creerBon(
            entreeCreation(
              envoye,
              assetId !== null ? { assetId } : { siteId: siteId ?? '' },
              compteursEnvoyes,
              cleDeCreation.current,
            ),
          );
      // Un bon CRÉÉ à l'instant n'a pas encore d'intervention où écrire les heures : elles partent juste après, avec sa révision.
      const heures = heuresEnvoyees(envoye.heures);
      if (bon === null && heures.length > 0) {
        enregistre = await api.sauvegarderBon(enregistre.id, { revision: enregistre.revision, heures });
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setConflit(true);
      else setErreurApi(e instanceof Error ? e : new Error('Enregistrement impossible.'));
      setEnCours(false);
      return;
    }

    if (!soumettre) {
      onEnregistre({ bon: enregistre, creation: bon === null, soumis: false });
      setEnCours(false);
      return;
    }

    // Le brouillon est enregistré : si la soumission est refusée, il reste en brouillon, rien n'est perdu.
    try {
      const soumis = await api.soumettreBon(enregistre.id, enregistre.revision);
      onEnregistre({ bon: soumis, creation: bon === null, soumis: true });
    } catch (e) {
      onEnregistre({
        bon: enregistre,
        creation: bon === null,
        soumis: false,
        refusSoumission: e instanceof Error ? e : new Error('Soumission impossible.'),
      });
    } finally {
      setEnCours(false);
    }
  };

  const fermer = () => {
    if (modifie && !window.confirm('Abandonner vos modifications non enregistrées ?')) return;
    onFermer();
  };

  const natureAChoisir = naturesProposees.length === 0;

  /** Choisir (ou changer) la nature : ses contrôles, son modèle. Ce que le nouveau modèle ne porte pas est retiré. */
  const choisirNature = (id: string) => {
    const choisie = natures.find((n) => n.id === id);
    const nouvelleConfig = configDe(modeleDe(choisie));
    modifier((b) => ({
      ...b,
      natureId: id,
      // Une nature qui n'enregistre pas d'entretien (ou un modèle sans palier) ne peut pas porter de palier : on le retire.
      levelKey: choisie?.recordsService && nouvelleConfig.palier ? b.levelKey : null,
      // Un modèle sans relevé ni suite (la livraison, la location) n'a nulle part où les écrire : ils partent avec la rubrique.
      valeursReleves: nouvelleConfig.releves ? b.valeursReleves : {},
      suites: nouvelleConfig.suites ? b.suites : [],
      // Les contrôles suivent la nature : ceux de la nouvelle liste, ce qui était coché restant coché pour la même clé.
      controles: controlesDeLaNature(choisie, b.controles),
    }));
  };

  // ------------------------------------------------------------------- les rubriques

  const rubriqueConstat = (
    <section className="carte" key="constat">
      <header className="entete-carte">
        <h3>Constat</h3>
      </header>
      <div className="carte-corps">
        <div className="champ">
          <label htmlFor="bon-probleme">
            Problème signalé <span className="requis" title="Obligatoire pour soumettre"> *</span>
          </label>
          <textarea
            id="bon-probleme"
            rows={3}
            maxLength={4000}
            value={courant.details.problemeSignale}
            disabled={enCours}
            placeholder="Ce que le client a signalé à l’appel…"
            onChange={(e) => majDetails({ problemeSignale: e.target.value })}
          />
        </div>
        <div className="champ">
          <label htmlFor="bon-travaux">
            Travaux réalisés <span className="requis" title="Obligatoire pour soumettre"> *</span>
          </label>
          <textarea
            id="bon-travaux"
            rows={3}
            maxLength={4000}
            value={courant.details.travauxRealises}
            disabled={enCours}
            placeholder="Cause trouvée, ce qui a été fait…"
            onChange={(e) => majDetails({ travauxRealises: e.target.value })}
          />
        </div>
      </div>
    </section>
  );

  const rubriqueControles =
    courant.controles.length > 0 || (nature?.checkItems.length ?? 0) > 0 ? (
      <section className="carte" key="controles">
        <header className="entete-carte">
          <h3>{config.titreControles}</h3>
          {courant.controles.length > 0 && (
            <span className="aide">
              {nombreControlesFaits(courant.controles)} sur {courant.controles.length} faits
            </span>
          )}
        </header>
        <div className="carte-corps">
          {courant.controles.length === 0 ? (
            <div className="saisie-ligne">
              <button
                type="button"
                className="secondaire"
                disabled={enCours}
                onClick={() => modifier((b) => ({ ...b, controles: controlesDeLaNature(nature, b.controles) }))}
              >
                Ajouter les contrôles de cette nature
              </button>
              <span className="aide">Ce bon n’en porte pas encore (il a été commencé avant leur arrivée).</span>
            </div>
          ) : (
            <>
              <ul className="controles-bon" aria-label="Contrôles de la nature">
                {courant.controles.map((c) => (
                  <li key={c.checkKey}>
                    <label className="controle-bon">
                      <input
                        type="checkbox"
                        checked={c.checked}
                        disabled={enCours}
                        onChange={() => modifier((b) => basculerControle(b, c.checkKey))}
                      />
                      <span>{c.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <div className="saisie-ligne">
                <button type="button" className="secondaire" disabled={enCours} onClick={() => modifier((b) => cocherTousLesControles(b, true))}>
                  Tout cocher
                </button>
                <button type="button" className="secondaire discret" disabled={enCours} onClick={() => modifier((b) => cocherTousLesControles(b, false))}>
                  Tout décocher
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    ) : null;

  const rubriqueValeurs =
    config.releves && assetId !== null ? (
      <section className="carte" key="valeurs">
        <header className="entete-carte">
          <h3>{config.titreValeurs}</h3>
        </header>
        <div className="carte-corps">
          {config.departRetour && (
            <p className="aide">
              Les valeurs saisies ici sont celles du <strong>départ</strong> : le papier garde une ligne « au retour » à compléter à la
              main.
            </p>
          )}
          {chargement && <div className="vide">Lecture des compteurs de la machine…</div>}
          {!chargement && compteursIndisponibles && <div className="message info">{compteursIndisponibles}</div>}
          {!chargement && !compteursIndisponibles && !compteurs.some((c) => estReleveEnHeures(c.unit, c.label)) && (
            <div className="message info">
              Cette machine n’a aucun compteur d’heures : créez « Heures totales » et « Heures en charge » depuis sa fiche dans Le
              Parc. Un bon sans heures reste possible.
            </div>
          )}
          {lignesCompteurs.length > 0 && (
            <table className="tableau-bon">
              <thead>
                <tr>
                  <th>Compteur</th>
                  <th>Dernier relevé connu</th>
                  <th>Valeur lue ce jour-là</th>
                </tr>
              </thead>
              <tbody>
                {lignesCompteurs.map((c) => {
                  const texte = courant.valeursReleves[c.id] ?? '';
                  const lecture = lireDecimal(texte, OPTIONS_RELEVE);
                  return (
                    <tr key={c.id}>
                      <td>
                        {c.label}
                        {c.unit ? <span className="aide"> ({c.unit})</span> : null}
                        {c.drivesMaintenance && <span className="badge badge-propriete" title="Commande les entretiens de la machine"> entretiens</span>}
                        {estCompteurACreer(c) && (
                          <span
                            className="badge badge-propriete"
                            title="La machine n’a pas encore ce compteur : il sera créé sur sa fiche à l’enregistrement du bon, si une valeur y est saisie."
                          >
                            {' '}
                            nouveau
                          </span>
                        )}
                      </td>
                      <td className="aide">
                        {c.dernier ? `${valeurAffichee(c.dernier.value)} le ${jourFr(c.dernier.readAt)}` : 'aucun relevé'}
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Valeur du compteur ${c.label}`}
                          className={lecture.ok ? '' : 'invalide'}
                          value={texte}
                          disabled={enCours}
                          placeholder="—"
                          onChange={(e) => modifier((b) => majReleve(b, c.id, e.target.value))}
                        />
                        {!lecture.ok && <p className="aide erreur-champ">{lecture.erreur}</p>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {nature?.recordsService && config.palier && (
            <div className="champ palier-realise">
              <label htmlFor="bon-palier">Palier de la gamme réalisé pendant cette intervention</label>
              <select
                id="bon-palier"
                value={courant.levelKey ?? ''}
                disabled={enCours || paliers.length === 0}
                onChange={(e) => modifier((b) => ({ ...b, levelKey: e.target.value === '' ? null : e.target.value }))}
              >
                <option value="">— Aucun —</option>
                {paliers.map((p) => (
                  <option key={p.levelKey} value={p.levelKey}>
                    Palier {p.code} — {p.label}
                  </option>
                ))}
              </select>
              <p className="aide">
                {paliers.length === 0
                  ? 'Aucune gamme n’est rattachée à cette machine (ou elle n’a aucune version en vigueur) : le palier ne peut pas être déclaré.'
                  : 'Jamais présélectionné : un bon Praxedo ne dit pas le palier, c’est à vous de le choisir. Il exige les heures du compteur des entretiens ci-dessus.'}
              </p>
            </div>
          )}
        </div>
      </section>
    ) : null;

  const rubriqueDossier = (
    <section className="carte" key="dossier">
      <header className="entete-carte">
        <h3>Dossier technique et certificats</h3>
      </header>
      <div className="carte-corps">
        <div className="champ">
          <label htmlFor="bon-dossier">Où le dossier a-t-il été laissé ?</label>
          <select
            id="bon-dossier"
            value={courant.details.dossier}
            disabled={enCours}
            onChange={(e) => majDetails({ dossier: e.target.value as ChoixDossier | '' })}
          >
            <option value="">— Non précisé —</option>
            {CHOIX_DOSSIER.map((c) => (
              <option key={c} value={c}>
                Dossier technique et certificats {LIBELLE_DOSSIER[c]}
              </option>
            ))}
          </select>
          <p className="aide">Jamais présélectionné : c’est un choix à faire sur place.</p>
        </div>
      </div>
    </section>
  );

  const rubriquePeriode = (
    <section className="carte" key="periode">
      <header className="entete-carte">
        <h3>Période de location</h3>
      </header>
      <div className="carte-corps">
        <div className="grille-bon">
          <div className="champ">
            <label htmlFor="bon-location-du">Mise à disposition le</label>
            <input
              id="bon-location-du"
              type="date"
              value={courant.details.locationDu}
              disabled={enCours}
              onChange={(e) => majDetails({ locationDu: e.target.value })}
            />
          </div>
          <div className="champ">
            <label htmlFor="bon-location-au">Retour prévu le</label>
            <input
              id="bon-location-au"
              type="date"
              min={courant.details.locationDu || undefined}
              value={courant.details.locationAu}
              disabled={enCours}
              onChange={(e) => majDetails({ locationAu: e.target.value })}
            />
          </div>
        </div>
      </div>
    </section>
  );

  const colonnesSerie = config.serie !== 'AUCUNE';
  const rubriqueLignes = (
    <section className="carte" key="lignes">
      <header className="entete-carte">
        <h3>{config.titreLignes}</h3>
      </header>
      <div className="carte-corps">
        {config.genres.includes('PART') && assetId !== null && !chargement && (
          <>
            <BlocGammeBon
              maintenance={maintenance}
              constructeurId={constructeurId}
              levelKeyMachine={courant.levelKey}
              palierSynchronise={Boolean(nature?.recordsService && config.palier)}
              onChoisirPalierRealise={(levelKey) => modifier((b) => ({ ...b, levelKey }))}
              onVerser={(niveau, gamme) => void verserPieces(niveau, gamme)}
              desactive={enCours}
              enCours={controleEnCours}
            />
            {messageGamme && <div className={`message ${messageGamme.attention ? 'avertissement' : 'info'}`}>{messageGamme.texte}</div>}
          </>
        )}
        {courant.lignes.length === 0 && (
          <div className="vide">
            {modele === 'DELIVERY'
              ? 'Aucun matériel. Ajoutez le matériel livré, avec son numéro de série.'
              : modele === 'RENTAL'
                ? 'Aucun accessoire. Ajoutez ce qui a été remis avec la machine (flexible, câble…).'
                : 'Aucun article. Ajoutez les pièces, la main d’œuvre et le déplacement.'}
          </div>
        )}
        {courant.lignes.length > 0 && (
          <div className="tableau-defilant">
            <table className="tableau-bon tableau-bon-lignes">
              <thead>
                <tr>
                  {config.genres.length > 1 && <th>Type</th>}
                  {config.colonneReference && <th>Référence</th>}
                  <th>Désignation</th>
                  {colonnesSerie && <th>N° de série</th>}
                  <th>Quantité</th>
                  <th>Unité</th>
                  <th aria-label="Gestes" />
                </tr>
              </thead>
              <tbody>
                {courant.lignes.map((l, i) => {
                  const genres: readonly GenreLigneBon[] = config.genres.includes(l.kind) ? config.genres : [l.kind, ...config.genres];
                  // D'où vient CETTE ligne (enregistrée avec le brouillon) et, pour l'écran seulement, le contrôle de sa référence au tarif.
                  const origine = l.versee;
                  const verdict = verdictAffiche(l, verdicts[l.lineKey]);
                  return (
                    <tr key={l.lineKey} className={verdict ? (verdict.type === 'A_CHOISIR' ? 'ligne-gamme-a-choisir' : 'ligne-gamme-remplacee') : undefined}>
                      {config.genres.length > 1 && (
                        <td className="col-bon-type">
                          <select
                            aria-label={`Type de la ligne ${i + 1}`}
                            value={l.kind}
                            disabled={enCours}
                            onChange={(e) => modifier((b) => changerTypeLigne(b, i, e.target.value as GenreLigneBon))}
                          >
                            {genres.map((g) => (
                              <option key={g} value={g}>
                                {LIBELLE_GENRE_LIGNE[g]}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                      {config.colonneReference && (
                        <td className="col-bon-ref">
                          {l.kind === 'PART' ? (
                            // La liste par le DÉBUT de la référence (convention n° 8) : le tarif du fabricant de la gamme de la
                            // machine ; à défaut de gamme, celui de la MARQUE de la machine (constaté le 2026-10-17 : un CSD102
                            // KAESER sans gamme n'avait aucune aide). Sans machine (livraison sur un site), saisie entière.
                            <ChampReference
                              {...(gammeId ? { gammeId } : assetId ? { machineId: assetId } : {})}
                              libelle={`Référence de la ligne ${i + 1}`}
                              valeur={l.reference}
                              placeholder="Début de la référence"
                              desactive={enCours}
                              onChange={(valeur) => modifier((b) => majLigneBon(b, i, { reference: valeur }))}
                              onChoisir={(choix) =>
                                modifier((b) =>
                                  majLigneBon(b, i, {
                                    reference: choix.reference,
                                    // La désignation du tarif se propose, jamais par-dessus ce qui est déjà écrit.
                                    ...(l.designation.trim() === '' ? { designation: choix.description } : {}),
                                  }),
                                )
                              }
                            />
                          ) : l.kind === 'EQUIPMENT' ? (
                            <input
                              type="text"
                              aria-label={`Référence de la ligne ${i + 1}`}
                              maxLength={80}
                              value={l.reference}
                              disabled={enCours}
                              placeholder="Modèle ou référence"
                              onChange={(e) => modifier((b) => majLigneBon(b, i, { reference: e.target.value }))}
                            />
                          ) : (
                            <span className="aide">—</span>
                          )}
                        </td>
                      )}
                      <td>
                        <input
                          type="text"
                          aria-label={`Désignation de la ligne ${i + 1}`}
                          maxLength={300}
                          value={l.designation}
                          disabled={enCours}
                          onChange={(e) => modifier((b) => majLigneBon(b, i, { designation: e.target.value }))}
                        />
                        {origine && <span className="pastille-gamme">gamme {origine.palier}</span>}
                        {!origine && estEnPlus(l.kind) && <span className="pastille-en-plus">en plus</span>}
                        {verdict && (
                          <span className={verdict.type === 'A_CHOISIR' ? 'verdict-gamme alerte' : 'verdict-gamme ok'}>
                            {verdict.type === 'A_CHOISIR' ? '⚠ ' : '✔ '}
                            {phraseDuVerdict(verdict)}
                          </span>
                        )}
                        {verdict?.type === 'A_CHOISIR' && verdict.candidats.length > 0 && (
                          <span className="candidats-gamme">
                            {verdict.candidats.map((c) => (
                              <button
                                key={c.reference}
                                type="button"
                                className="secondaire discret"
                                title={c.description}
                                disabled={enCours}
                                onClick={() => choisirCandidat(l.lineKey, c)}
                              >
                                {c.reference} — {c.description}
                              </button>
                            ))}
                          </span>
                        )}
                      </td>
                      {colonnesSerie && (
                        <td className="col-bon-serie">
                          {l.kind === 'EQUIPMENT' ? (
                            <input
                              type="text"
                              aria-label={`N° de série de la ligne ${i + 1}`}
                              className="champ-court"
                              maxLength={80}
                              value={l.serie}
                              disabled={enCours}
                              placeholder="—"
                              onChange={(e) => modifier((b) => majLigneBon(b, i, { serie: e.target.value }))}
                            />
                          ) : (
                            <span className="aide">—</span>
                          )}
                        </td>
                      )}
                      <td className="col-bon-qte">
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Quantité de la ligne ${i + 1}`}
                          className={`champ-court ${lireDecimal(l.quantite, { negatifPermis: false, chiffresEntiers: 11, decimales: 3 }).ok ? '' : 'invalide'}`}
                          value={l.quantite}
                          disabled={enCours}
                          onChange={(e) => modifier((b) => majLigneBon(b, i, { quantite: e.target.value }))}
                        />
                      </td>
                      <td className="col-bon-qte">
                        <input
                          type="text"
                          aria-label={`Unité de la ligne ${i + 1}`}
                          className="champ-court"
                          maxLength={20}
                          value={l.unite}
                          disabled={enCours}
                          placeholder="—"
                          onChange={(e) => modifier((b) => majLigneBon(b, i, { unite: e.target.value }))}
                        />
                      </td>
                      <td className="gestes-ligne">
                        <button type="button" className="secondaire discret" title="Monter" disabled={enCours || i === 0} onClick={() => modifier((b) => deplacerLigneBon(b, i, -1))}>
                          ↑
                        </button>
                        <button type="button" className="secondaire discret" title="Descendre" disabled={enCours || i === courant.lignes.length - 1} onClick={() => modifier((b) => deplacerLigneBon(b, i, 1))}>
                          ↓
                        </button>
                        <button type="button" className="secondaire discret" title="Retirer la ligne" disabled={enCours} onClick={() => modifier((b) => retirerLigneBon(b, i))}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="saisie-ligne">
          {config.boutonsLignes.map((b) => (
            <button key={b.kind} type="button" className="secondaire" disabled={enCours} onClick={() => modifier((bon) => ajouterLigneBon(bon, b.kind))}>
              {b.libelle}
            </button>
          ))}
        </div>
      </div>
    </section>
  );

  const rubriqueReserves = (
    <section className="carte" key="reserves">
      <header className="entete-carte">
        <h3>Réserves du client</h3>
      </header>
      <div className="carte-corps">
        <textarea
          aria-label="Réserves du client"
          rows={3}
          maxLength={4000}
          value={courant.details.reserves}
          disabled={enCours}
          placeholder="Réserves formulées par le client à la réception (laissez vide s’il n’y en a pas)…"
          onChange={(e) => majDetails({ reserves: e.target.value })}
        />
      </div>
    </section>
  );

  const rubriqueSuites = config.suites ? (
    <section className="carte" key="suites">
      <header className="entete-carte">
        <h3>Quelque chose à prévoir ?</h3>
      </header>
      <div className="carte-corps">
        <p className="aide">
          Une seule question, une seule liste : à la validation du bon, chaque suite arrive dans « Suites à traiter » (menu
          de gauche). Rien n’est créé automatiquement (devis, visite, commande) : elle reste ouverte jusqu’à ce qu’on la
          traite à la main.
        </p>
        {assetId === null && <div className="message info">Sans machine, une suite à donner n’a nulle part où s’écrire.</div>}
        {courant.suites.length === 0 && <div className="vide">Rien à prévoir.</div>}
        {courant.suites.map((s, i) => (
          <div className="suite-bon" key={s.followUpKey}>
            <select
              aria-label={`Type de la suite ${i + 1}`}
              value={s.kind}
              disabled={enCours}
              onChange={(e) => modifier((b) => majSuite(b, i, { kind: e.target.value as GenreSuiteBon }))}
            >
              {GENRES_SUITE.map((g) => (
                <option key={g} value={g}>
                  {LIBELLE_SUITE[g]}
                </option>
              ))}
            </select>
            <input
              type="text"
              aria-label={`Commentaire de la suite ${i + 1}`}
              maxLength={1000}
              value={s.comment}
              disabled={enCours}
              placeholder="Que faut-il prévoir ? (obligatoire)"
              onChange={(e) => modifier((b) => majSuite(b, i, { comment: e.target.value }))}
            />
            <button type="button" className="secondaire discret" title="Retirer la suite" disabled={enCours} onClick={() => modifier((b) => retirerSuite(b, i))}>
              ✕
            </button>
          </div>
        ))}
        <div className="saisie-ligne">
          <button type="button" className="secondaire" disabled={enCours} onClick={() => modifier((b) => ajouterSuite(b))}>
            + Ajouter une suite à donner
          </button>
        </div>
      </div>
    </section>
  ) : null;

  const rubriqueObservations = (
    <section className="carte" key="observations">
      <header className="entete-carte">
        <h3>Observations</h3>
      </header>
      <div className="carte-corps">
        <textarea
          aria-label="Observations"
          rows={4}
          maxLength={4000}
          value={courant.observations}
          disabled={enCours}
          placeholder="Ce qui s’est passé, ce qui a été constaté…"
          onChange={(e) => modifier((b) => ({ ...b, observations: e.target.value }))}
        />
      </div>
    </section>
  );

  // Les heures de l'équipe (2026-10-24) : une ligne par technicien et par jour, les mêmes que celles du planning, écrites dans
  // l'équipe de l'intervention à l'enregistrement. Le jour du bon propose une ligne vide pour chaque technicien du bon.
  const lignesDesHeures = lignesHeures(courant.heures, courant.techniciens, courant.performedOn);
  const total = totalMinutes(courant.heures);
  const rubriqueHeures = (
    <section className="carte" key="heures">
      <header className="entete-carte">
        <h3>Heures de l’équipe</h3>
      </header>
      <div className="carte-corps">
        {lignesDesHeures.length === 0 ? (
          <div className="vide">Choisissez la date et les techniciens du bon : une ligne d’heures apparaît pour chacun.</div>
        ) : (
          <div className="tableau-defilant">
            <table className="tableau tableau-heures-bon">
              <thead>
                <tr>
                  <th>Jour</th>
                  <th>Technicien</th>
                  <th>Heures</th>
                  <th className="montant">Facturé au quart d’heure</th>
                  <th aria-label="Gestes" />
                </tr>
              </thead>
              <tbody>
                {lignesDesHeures.map((h, i) => {
                  const index = h.proposee ? -1 : i;
                  const ajoutee = !h.proposee && !h.enregistree;
                  const lecture = lireHeuresSaisies(h.duree);
                  return (
                    <tr key={h.proposee ? `p-${h.jour}-${cleNomTechnicien(h.technicien)}` : `h-${i}`}>
                      <td>
                        {ajoutee ? (
                          <input
                            type="date"
                            aria-label={`Jour de la ligne d’heures ${i + 1}`}
                            max={aujourdhui}
                            value={h.jour}
                            disabled={enCours}
                            onChange={(e) => modifier((b) => ({ ...b, heures: majLigneAjoutee(b.heures, index, { jour: e.target.value }) }))}
                          />
                        ) : (
                          jourFr(h.jour)
                        )}
                      </td>
                      <td>
                        {ajoutee ? (
                          <select
                            aria-label={`Technicien de la ligne d’heures ${i + 1}`}
                            value={h.technicien}
                            disabled={enCours}
                            onChange={(e) => modifier((b) => ({ ...b, heures: majLigneAjoutee(b.heures, index, { technicien: e.target.value }) }))}
                          >
                            <option value="">— Choisir —</option>
                            {courant.techniciens.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        ) : (
                          h.technicien
                        )}
                      </td>
                      <td>
                        {h.pointee === true ? (
                          <span className="heures-pointees" title="Calculées d’après les pointages du technicien : corrigez un pointage (carte « Pointages »).">
                            {h.duree} <span className="aide">d’après les pointages — corriger un pointage</span>
                          </span>
                        ) : (
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Heures de ${h.technicien || 'la ligne ' + String(i + 1)} le ${jourFr(h.jour)}`}
                          className={`champ-court ${lecture === 'ERREUR' ? 'invalide' : ''}`}
                          value={h.duree}
                          disabled={enCours}
                          placeholder="ex. 1,75 ou 1h45"
                          onChange={(e) => modifier((b) => ({ ...b, heures: majDuree(b.heures, h.jour, h.technicien, e.target.value) }))}
                        />
                        )}
                      </td>
                      <td className="montant aide">{quartDHeure(lecture)}</td>
                      <td className="gestes-ligne">
                        {!h.proposee && h.pointee !== true && (
                          <button
                            type="button"
                            className="secondaire discret"
                            title={h.enregistree ? 'Effacer ces heures' : 'Retirer la ligne'}
                            disabled={enCours}
                            onClick={() => modifier((b) => ({ ...b, heures: retirerLigneHeures(b.heures, index) }))}
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="saisie-ligne">
          <button
            type="button"
            className="secondaire"
            disabled={enCours || courant.techniciens.length === 0}
            onClick={() => modifier((b) => ({ ...b, heures: ajouterLigneHeures(b.heures, '', b.techniciens[0] ?? '') }))}
          >
            + Autre jour
          </button>
          {total > 0 && <span className="aide">Total saisi : {dureeFr(total)}</span>}
        </div>
        <p className="aide">
          Heures réellement passées, par technicien et par jour. Elles sont arrondies au quart d’heure supérieur à la validation.
          Elles restent enregistrées pour la charge et la rentabilité, même quand le contrat annuel les comprend. Une main d’œuvre
          exceptionnelle à facturer en plus (un nettoyage, par exemple) se met avec « + Main d’œuvre en plus » dans les articles.
        </p>
      </div>
    </section>
  );

  /** La rubrique de l'éditeur pour une rubrique de la feuille ; `null` quand elle n'a pas d'objet à la saisie (« entretien réalisé » est le palier de la rubrique des valeurs). */
  const rubrique = (r: Rubrique): ReactNode => {
    switch (r) {
      case 'probleme':
        return rubriqueConstat;
      case 'travaux':
        return null; // dans la même carte que le problème signalé
      case 'controles':
        return rubriqueControles;
      case 'valeurs':
        return rubriqueValeurs;
      case 'realise':
      case 'miseEnService':
        return null;
      case 'dossier':
        return rubriqueDossier;
      case 'periode':
        return rubriquePeriode;
      case 'lignes':
        return rubriqueLignes;
      case 'reserves':
        return rubriqueReserves;
      case 'suites':
        return rubriqueSuites;
      case 'observations':
        return rubriqueObservations;
    }
  };

  return (
    <div className="editeur-bon">
      <div className="message info">
        {bon && interventionAFaire(bon.status) ? (
          <>
            Bon de l’intervention <strong>{bon.number}</strong>, prévue au planning
            {bon.planification.jours.length > 0 && (
              <>
                {' '}
                {bon.planification.jours.length > 1
                  ? `du ${jourFr(bon.planification.jours[0])} au ${jourFr(bon.planification.jours[bon.planification.jours.length - 1])}`
                  : `le ${jourFr(bon.planification.jours[0])}`}
              </>
            )}
            {bon.planification.equipe.length > 0 && <> avec {bon.planification.equipe.join(', ')}</>}.{' '}
            {jourProposeDuPlanning(bon.planification.jours, aujourdhuiLocal()) > aujourdhuiLocal()
              ? 'Le jour prévu n’est pas encore passé : il est proposé pour préparer le bon, qui ne pourra être soumis qu’à partir de ce jour. L’équipe prévue est proposée.'
              : 'Le jour et l’équipe prévus sont proposés : changez-les si l’intervention s’est passée autrement.'}{' '}
            Tant qu’il n’est pas validé, ce bon n’écrit rien dans les
            compteurs ni dans l’entretien de la machine.
          </>
        ) : bon ? (
          <>
            Vous modifiez le brouillon du bon <strong>{bon.number}</strong>. Tant qu’il n’est pas validé, il n’écrit rien
            dans les compteurs ni dans l’entretien de la machine.
          </>
        ) : assetId !== null ? (
          <>
            Nouveau bon sur <strong>{libelleCible}</strong>. Son numéro est attribué à la première
            sauvegarde. Un bon se saisit après coup : <strong>la date de l’intervention n’est pas préremplie</strong>.
          </>
        ) : (
          <>
            Nouvelle livraison sur le site <strong>{libelleCible}</strong>, <strong>sans machine</strong>. Son numéro est attribué à la
            première sauvegarde.
          </>
        )}
      </div>

      <section className="carte">
        <header className="entete-carte">
          <h3>En-tête</h3>
        </header>
        <div className="carte-corps">
          <div className="grille-bon">
            <div className="champ">
              <label htmlFor="bon-nature">Nature du bon <span className="requis" title="Obligatoire">*</span></label>
              <select
                id="bon-nature"
                value={courant.natureId}
                disabled={enCours || natureAChoisir}
                onChange={(e) => choisirNature(e.target.value)}
              >
                <option value="">— Choisir —</option>
                {naturesProposees.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.label}
                  </option>
                ))}
              </select>
              {natureAChoisir && assetId !== null && (
                <p className="aide">Aucune nature n’existe : installez les natures de départ depuis la liste des bons.</p>
              )}
              {natureAChoisir && assetId === null && (
                <p className="aide">Aucune nature de livraison n’existe : installez les natures de départ depuis la liste des bons.</p>
              )}
              {nature && !config.releves && (
                <p className="aide">Cette nature ne relève aucun compteur et ne laisse aucune suite à donner.</p>
              )}
            </div>

            <div className="champ">
              <span className="libelle" id="bon-facturation">
                Comment se facture cette intervention ? <span className="requis" title="Obligatoire pour soumettre"> *</span>
              </span>
              {/* Deux boutons, comme sur l'ébauche validée (2026-10-24) : aucun n'est choisi d'avance. */}
              <div className="choix-facturation" role="radiogroup" aria-labelledby="bon-facturation">
                {FACTURATIONS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    role="radio"
                    aria-checked={courant.facturation === f}
                    className={courant.facturation === f ? 'secondaire choisi' : 'secondaire'}
                    disabled={enCours}
                    onClick={() => modifier((b) => ({ ...b, facturation: f as FacturationBon }))}
                  >
                    {courant.facturation === f ? '✓ ' : ''}
                    {LIBELLE_FACTURATION[f]}
                  </button>
                ))}
              </div>
              <p className="aide">
                {courant.facturation === 'CONTRACT'
                  ? 'Contrat annuel : la visite est comprise, seuls les « en plus » se facturent.'
                  : courant.facturation === 'BILLABLE'
                    ? 'Facturé à la visite : la gamme, la main d’œuvre et le déplacement se facturent, les « en plus » aussi.'
                    : 'Jamais présélectionné : contrat annuel (la visite est comprise) ou facturé à la visite.'}
              </p>
            </div>

            <div className="champ">
              <label htmlFor="bon-date">Date de l’intervention <span className="requis" title="Obligatoire pour soumettre"> *</span></label>
              <input
                id="bon-date"
                type="date"
                {...(contexte.dateFuturePermise ? {} : { max: aujourdhui })}
                value={courant.performedOn}
                disabled={enCours}
                onChange={(e) => modifier((b) => ({ ...b, performedOn: e.target.value }))}
              />
            </div>

            <div className="champ champ-large champ-techniciens">
              <label htmlFor="bon-technicien-ajout">
                {libelleTechniciens(modele, 2)} <span className="requis" title="Au moins un pour soumettre"> *</span>
              </label>
              {courant.techniciens.length > 0 && (
                <ul className="pastilles-techniciens" aria-label="Techniciens de l’intervention">
                  {courant.techniciens.map((nom) => (
                    <li key={cleNomTechnicien(nom)} className="pastille-technicien">
                      <span>{nom}</span>
                      <button
                        type="button"
                        className="pastille-retirer"
                        aria-label={`Retirer ${nom}`}
                        title="Retirer ce technicien"
                        disabled={enCours}
                        onClick={() => modifier((b) => retirerTechnicien(b, nom))}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {autreNom === null ? (
                <select
                  id="bon-technicien-ajout"
                  value=""
                  disabled={enCours || courant.techniciens.length >= PLAFOND_TECHNICIENS}
                  onChange={(e) => {
                    const choix = e.target.value;
                    if (choix === CHOIX_AUTRE_TECHNICIEN) setAutreNom('');
                    else if (choix !== '') modifier((b) => ajouterTechnicien(b, choix));
                  }}
                >
                  <option value="">{courant.techniciens.length === 0 ? '— Choisir un technicien —' : '+ Ajouter un technicien'}</option>
                  {techniciensAChoisir.map((nom) => (
                    <option key={nom} value={nom}>
                      {nom}
                    </option>
                  ))}
                  <option value={CHOIX_AUTRE_TECHNICIEN}>Autre technicien…</option>
                </select>
              ) : (
                <div className="saisie-ligne">
                  <input
                    id="bon-technicien-autre"
                    type="text"
                    maxLength={100}
                    autoFocus
                    value={autreNom}
                    disabled={enCours}
                    placeholder="Prénom et nom du technicien"
                    aria-label="Nom du technicien à ajouter"
                    onChange={(e) => setAutreNom(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        validerAutreTechnicien();
                      } else if (e.key === 'Escape') {
                        setAutreNom(null);
                      }
                    }}
                  />
                  <button type="button" className="secondaire" disabled={enCours} onClick={validerAutreTechnicien}>
                    Ajouter
                  </button>
                  <button type="button" className="secondaire discret" disabled={enCours} onClick={() => setAutreNom(null)}>
                    Annuler
                  </button>
                </div>
              )}
              <p className="aide">
                Une intervention n’est pas toujours faite seul : ajoutez-les un par un. Un nom absent du menu s’ajoute par
                « Autre technicien… » et sera proposé ensuite.
              </p>
            </div>

            <div className="champ">
              <label htmlFor="bon-praxedo">N° Praxedo</label>
              <input
                id="bon-praxedo"
                type="text"
                maxLength={40}
                value={courant.praxedoNumber}
                disabled={enCours}
                placeholder="Pour reprendre un ancien bon"
                onChange={(e) => modifier((b) => ({ ...b, praxedoNumber: e.target.value }))}
              />
            </div>

            <div className="champ champ-large">
              <label htmlFor="bon-contact">Contact sur place</label>
              <input
                id="bon-contact"
                type="text"
                maxLength={200}
                value={courant.contactLabel}
                disabled={enCours}
                placeholder="Nom, téléphone — saisi à la main, jamais copié d’une fiche"
                onChange={(e) => modifier((b) => ({ ...b, contactLabel: e.target.value }))}
              />
            </div>
          </div>
        </div>
      </section>

      {courant.natureId !== '' && rubriqueHeures}

      {courant.natureId === '' ? (
        // Tant que la nature n'est pas choisie, aucune feuille n'est connue : on ne montre pas celle d'un modèle par défaut.
        <div className="carte">
          <div className="vide">Choisissez la nature du bon : l’éditeur montre alors les rubriques de son modèle de feuille.</div>
        </div>
      ) : (
        config.ordre.map((r) => <Fragment key={r}>{rubrique(r)}</Fragment>)
      )}

      {/* Le chiffrage dans le bon (ébauche du 2026-10-24) : d'après ce qui est ENREGISTRÉ, pour qui voit les prix. */}
      {bon && droitsBons.peutVoirChiffrage && courant.natureId !== '' && (
        <ChiffrageBon interventionId={bon.id} cle={bon.revision} palier={palierVerse(courant.lignes)} />
      )}
      {bon && droitsBons.peutVoirChiffrage && modifie && (
        <p className="aide">Le chiffrage ci-dessus est celui du dernier enregistrement : enregistrez pour voir vos modifications chiffrées.</p>
      )}

      <div className="editeur-barre">
        {problemes.length > 0 && (
          <div className="message erreur">
            Corrigez {problemes.length} point{problemes.length > 1 ? 's' : ''} avant de continuer :
            <ul className="details-erreur">
              {problemes.slice(0, 10).map((p, i) => (
                <li key={`${p}-${i}`}>{p}</li>
              ))}
              {problemes.length > 10 && <li>… et {problemes.length - 10} autre{problemes.length - 10 > 1 ? 's' : ''}.</li>}
            </ul>
          </div>
        )}
        <ErreurFormulaire
          message={erreurApi?.message ?? null}
          details={erreurApi instanceof ApiError ? erreurApi.details : undefined}
        />
        {conflit && (
          <div className="message erreur">
            Ce bon a été modifié par quelqu’un d’autre depuis votre ouverture : rechargez-le avant d’enregistrer, sinon
            vous écraseriez son travail.{' '}
            <button type="button" className="secondaire" onClick={onConflit}>
              Recharger le bon (vos modifications non enregistrées seront perdues)
            </button>
          </div>
        )}
        <div className="editeur-barre-actions">
          <span className={modifie ? 'etat attente' : 'aide'}>
            {modifie ? 'Modifications non enregistrées' : bon ? 'Aucune modification' : 'Bon non enregistré'}
          </span>
          <div className="saisie-ligne">
            <button type="button" className="secondaire" disabled={enCours} onClick={fermer}>
              {modifie ? 'Abandonner les modifications' : 'Fermer l’éditeur'}
            </button>
            <button
              type="button"
              className="secondaire"
              disabled={enCours || chargement || (bon !== null && !modifie)}
              onClick={() => void enregistrer(false)}
            >
              {enCours ? 'Enregistrement…' : bon ? 'Enregistrer le brouillon' : 'Enregistrer (attribue le numéro)'}
            </button>
            <button type="button" disabled={enCours || chargement} onClick={() => void enregistrer(true)}>
              {enCours ? 'Enregistrement…' : 'Enregistrer et soumettre à validation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
